/**
 * AdminLayout Component
 *
 * Specialized layout component for admin screens (beta application management, user management).
 * Extends AppLayout with admin-specific configuration for streamlined admin experience.
 */

// 1. External dependencies
import React, { useState, useCallback } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Settings, Users, FileText, LayoutDashboard, Menu, Pin, PinOff } from 'lucide-react';

// 2. Internal absolute imports
import { Card, CardBody, CardHeader, Link, Button, Drawer, DrawerContent, DrawerHeader, DrawerBody, useDisclosure } from '@enfomentis/ui';
import { AppLayout } from './AppLayout';
import { useIsDesktop } from '@/hooks';

export interface AdminLayoutProps {
  /** Admin screen content */
  children: React.ReactNode;
  /** Optional page title */
  title?: string;
  /** Whether to show admin navigation */
  showAdminNavigation?: boolean;
  /** Custom header content */
  headerContent?: React.ReactNode;
}

/**
 * Administration Layout Component
 *
 * Provides specialized layout for admin screens:
 * - Extends AppLayout with admin-specific configuration
 * - Admin navigation sidebar for beta applications, users, settings
 * - Admin branding and styling
 * - Mobile-responsive admin interface
 *
 * Usage:
 * ```tsx
 * <AdminLayout title="Beta Applications" showAdminNavigation>
 *   <BetaApplicationManagement />
 * </AdminLayout>
 * ```
 */
export const AdminLayout: React.FC<AdminLayoutProps> = ({
  children,
  title,
  showAdminNavigation = true
}) => {
  const location = useLocation();

  // Mobile navigation drawer
  const [isNavigationDrawerOpen, setIsNavigationDrawerOpen] = useState<boolean>(false);

  // Desktop sidebar state
  const { isOpen: isDesktopDrawerOpen, onOpen: onDesktopDrawerOpen, onOpenChange: onDesktopDrawerOpenChange } = useDisclosure();
  const [isPinned, setIsPinned] = useState<boolean>(true);

  // Desktop detection
  const isDesktop = useIsDesktop();

  // Handle navigation selection
  const handleNavSelection = useCallback((): void => {
    // Auto-close drawer if not pinned
    if (!isPinned && isDesktopDrawerOpen) {
      onDesktopDrawerOpenChange();
    }
  }, [isPinned, isDesktopDrawerOpen, onDesktopDrawerOpenChange]);

  const handlePinToggle = useCallback((): void => {
    setIsPinned(prev => !prev);
  }, []);

  // Admin navigation items
  const adminNavItems = [
    {
      href: '/admin',
      icon: <LayoutDashboard size={18} />,
      label: 'Dashboard',
      isActive: location.pathname === '/admin'
    },
    {
      href: '/admin/beta-applications',
      icon: <FileText size={18} />,
      label: 'Beta Applications',
      isActive: location.pathname.startsWith('/admin/beta-applications')
    },
    {
      href: '/admin/users',
      icon: <Users size={18} />,
      label: 'Users',
      isActive: location.pathname.startsWith('/admin/users')
    },
    {
      href: '/admin/settings',
      icon: <Settings size={18} />,
      label: 'Settings',
      isActive: location.pathname.startsWith('/admin/settings')
    }
  ];

  return (
    <AppLayout
      maxWidth="full"
      customSubtitle="Administration Console"
      customMobileTitle="Admin"
    >
      <div className="flex h-full min-h-0">
        {/* Desktop Pinned Sidebar (Regular Card) */}
        {showAdminNavigation && isDesktop && isPinned && (
          <Card
            radius="none"
            shadow="sm"
            className="w-64 shrink-0 border-r border-default-200 flex flex-col"
          >
            <CardHeader className="border-b border-default-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-text-primary">Admin Navigation</h3>
              <Button
                variant="light"
                size="sm"
                isIconOnly
                onPress={handlePinToggle}
                title="Unpin sidebar"
              >
                <PinOff size={16} />
              </Button>
            </CardHeader>
            <CardBody className="p-2">
              <nav className="space-y-1">
                {adminNavItems.map((item) => (
                  <Link
                    key={item.href}
                    as={RouterLink}
                    href={item.href}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors w-full ${
                      item.isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-text-primary hover:bg-default-100'
                    }`}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                ))}
              </nav>
            </CardBody>
          </Card>
        )}

        {/* Desktop Unpinned Drawer (Overlay) */}
        {showAdminNavigation && isDesktop && !isPinned && (
          <Drawer
            isOpen={isDesktopDrawerOpen}
            onOpenChange={onDesktopDrawerOpenChange}
            placement="left"
            className="hidden md:block"
          >
            <DrawerContent>
              <DrawerHeader className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-text-primary">Admin Navigation</h3>
                <Button
                  variant="light"
                  size="sm"
                  isIconOnly
                  onPress={handlePinToggle}
                  title="Pin sidebar"
                  className="mr-4"
                >
                  <Pin size={16} />
                </Button>
              </DrawerHeader>
              <DrawerBody className="p-2">
                <nav className="space-y-1">
                  {adminNavItems.map((item) => (
                    <Link
                      key={item.href}
                      as={RouterLink}
                      href={item.href}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors w-full ${
                        item.isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-text-primary hover:bg-default-100'
                      }`}
                      onPress={handleNavSelection}
                    >
                      {item.icon}
                      {item.label}
                    </Link>
                  ))}
                </nav>
              </DrawerBody>
            </DrawerContent>
          </Drawer>
        )}


        {/* Floating Navigation Button (Mobile) */}
        {showAdminNavigation && (
          <div className="md:hidden fixed bottom-6 right-6 z-40">
            <Button
              variant="solid"
              color="primary"
              size="lg"
              isIconOnly
              onPress={() => setIsNavigationDrawerOpen(true)}
              title="Open Admin Menu"
              className="rounded-full shadow-lg"
            >
              <Menu size={24} />
            </Button>
          </div>
        )}

        {/* Navigation Drawer (Mobile) */}
        <Drawer
          isOpen={isNavigationDrawerOpen}
          onOpenChange={setIsNavigationDrawerOpen}
          placement="bottom"
          className="md:hidden"
        >
          <DrawerContent>
            <DrawerHeader>
              <h3 className="text-lg font-semibold text-text-primary">Admin Navigation</h3>
            </DrawerHeader>
            <DrawerBody className="pb-6">
              <nav className="space-y-2">
                {adminNavItems.map((item) => (
                  <Link
                    key={item.href}
                    as={RouterLink}
                    href={item.href}
                    className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors w-full ${
                      item.isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-text-primary hover:bg-default-100'
                    }`}
                    onPress={() => setIsNavigationDrawerOpen(false)}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                ))}
              </nav>
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        {/* Main Content Area */}
        <div className="flex-1 min-w-0">
          <Card
            radius="none"
            shadow="none"
            className="h-full flex flex-col"
          >
            {title && (
              <CardHeader className="border-b border-default-200">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-3">
                    {/* Menu button when sidebar is collapsed (desktop) */}
                    {showAdminNavigation && isDesktop && !isPinned && (
                      <Button
                        variant="light"
                        size="sm"
                        isIconOnly
                        onPress={onDesktopDrawerOpen}
                        title="Open Admin Menu"
                      >
                        <Menu size={18} />
                      </Button>
                    )}
                    <h1 className="text-2xl font-semibold text-text-primary">
                      {title}
                    </h1>
                  </div>
                </div>
              </CardHeader>
            )}

            <CardBody className="flex-1 p-6 box-border overflow-y-auto overflow-x-hidden">
              {children}
            </CardBody>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
};